import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { Progress } from '@/components/ui/progress'
import { GenericBatchAnalysisModule } from '@/components/GenericBatchAnalysisModule'

import { ModuleAnalysesTab } from '@/components/ModuleAnalysesTab'
import { 
  ArrowLeft, 
  FileText, 
  Brain, 
  MessageSquare, 
  Calendar,
  User,
  AlertCircle,
  CheckCircle,
  Play,
  Download,
  Upload,
  Plus,
  Edit,
  Trash2,
  Clock,
  Target,
  TrendingUp,
  X,
  Link,
  RefreshCw,
  Eye,
  AlertTriangle,
  BarChart3
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { MembersManager } from '@/components/MembersManager'
import { toast } from '@/hooks/use-toast'
import { useUserRole } from '@/hooks/useUserRole'
import { useTranslation } from 'react-i18next'
import { formatWithUTC } from '@/lib/datetime'

interface Audit {
  id: string
  title: string
  description: string
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'initial_review' | 'detailed_analysis' | 'error'
  created_at: string
  updated_at: string
  user_id: string
  data_input: any
}

interface Document {
  id: string
  filename: string
  original_filename: string
  size_bytes: number
  created_at: string
  module_type?: string
  storage_path: string
  mime_type: string
  uploaded_by: string
  audit_id: string
  updated_at: string
}

interface Analysis {
  id: string
  type: 'initial' | 'detailed' | 'compliance'
  status: 'pending' | 'running' | 'completed' | 'failed'
  results?: any
  created_at: string
  completed_at?: string
}

interface Comment {
  id: string
  content: string
  author: string
  created_at: string
}

interface ActionItem {
  id: string
  title: string
  description: string
  priority: 'low' | 'medium' | 'high' | 'critical'
  assigned_to?: string
  due_date?: string
  status: 'open' | 'in_progress' | 'completed'
  created_at: string
  module_id?: string
}

interface Finding {
  id: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  title: string
  description: string
  evidence: string
  responsible: string
  status: 'open' | 'in_progress' | 'resolved'
  updated_at: string
  module_id: string
}

interface AuditFindingLite {
  id: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  finding_type: string
  description: string
  created_at: string
  data_reference?: Record<string, any>
  module_id?: string
}

interface FindingDecision {
  id: string
  finding_id: string
  decision_type: 'accepted' | 'needs_evidence' | 'dismissed'
  comment: string
  user_id: string
  user_role: string
  created_at: string
}

interface FindingComment {
  id: string
  finding_id: string
  content: string
  user_id: string
  user_name: string
  mentions: string[]
  attachments?: any
  created_at: string
}

interface ModuleSignoff {
  id: string
  audit_id: string
  module_id: string
  signed_off_by: string
  signed_off_at: string
  comment?: string
}

interface AuditSignoff {
  id: string
  audit_id: string
  signed_off_by: string
  signed_off_at: string
  comment?: string
}

interface Organization {
  id: string
  name: string
}

interface UserProfile {
  id: string
  full_name?: string
}

interface AuditMember {
  id: string
  user_id: string
  role: string
  added_at: string
  full_name?: string
  email?: string
  avatar_url?: string
}

interface CaseDetailPageProps {
  organization: Organization | null
  profile: UserProfile | null
}

const statusColors = {
  pending: 'bg-warning/10 text-warning border-warning/20',
  processing: 'bg-primary/10 text-primary border-primary/20',
  completed: 'bg-success/10 text-success border-success/20',
  failed: 'bg-destructive/10 text-destructive border-destructive/20',
  initial_review: 'bg-secondary/10 text-secondary border-secondary/20',
  detailed_analysis: 'bg-primary/10 text-primary border-primary/20',
  error: 'bg-destructive/10 text-destructive border-destructive/20'
}

const priorityColors = {
  low: 'bg-muted text-muted-foreground border-muted',
  medium: 'bg-warning/10 text-warning border-warning/20',
  high: 'bg-destructive/10 text-destructive border-destructive/20',
  critical: 'bg-destructive text-destructive-foreground border-destructive'
}

const modules = [
  { id: 'revenue', nameKey: 'modules.names.revenue' },
  { id: 'expenses', nameKey: 'modules.names.expenses' },
  { id: 'payroll', nameKey: 'modules.names.payroll' },
  { id: 'taxes_fees', nameKey: 'modules.names.taxes_fees' },
  { id: 'balance_sheet', nameKey: 'modules.names.balance_sheet' },
  { id: 'other_custom', nameKey: 'modules.names.other_custom' }
]

const moduleStatusLabels = {
  'not_started': 'Ej startad',
  'in_progress': 'Pågår', 
  'completed': 'Klart',
  'excluded': 'Exkluderad'
}

const moduleStatusColors = {
  'not_started': 'bg-muted text-muted-foreground border-muted',
  'in_progress': 'bg-warning/10 text-warning border-warning/20',
  'completed': 'bg-success/10 text-success border-success/20',
  'excluded': 'bg-secondary/10 text-secondary border-secondary/20'
}

export function CaseDetailPage({ organization, profile }: CaseDetailPageProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { role } = useUserRole()
  const { t } = useTranslation()
  
  const [audit, setAudit] = useState<Audit | null>(null)
  const [documents, setDocuments] = useState<Document[]>([])
  const [analyses, setAnalyses] = useState<Analysis[]>([])
  const [comments, setComments] = useState<Comment[]>([])
  const [actionItems, setActionItems] = useState<ActionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [newComment, setNewComment] = useState('')
  const [selectedModule, setSelectedModule] = useState<string | null>(null)
  const [selectedModuleTab, setSelectedModuleTab] = useState('overview')
  const [findings, setFindings] = useState<Finding[]>([])
  const [moduleStates, setModuleStates] = useState<Record<string, { status: keyof typeof moduleStatusLabels, documentsUploaded: number, documentsRequired: number }>>({})
  const [auditFindings, setAuditFindings] = useState<AuditFindingLite[]>([])
  
  // Analysis summary (per audit) derived from audit_findings
  const [analysisCount, setAnalysisCount] = useState<number>(0)
  const [analysisSeverity, setAnalysisSeverity] = useState<{ high: number; medium: number; low: number }>({ high: 0, medium: 0, low: 0 })
  
  const [findingDecisions, setFindingDecisions] = useState<FindingDecision[]>([])
  const [selectedFinding, setSelectedFinding] = useState<string | null>(null)
  const [decisionComment, setDecisionComment] = useState('')
  const [newFindingComment, setNewFindingComment] = useState<Record<string, string>>({})
  const [findingComments, setFindingComments] = useState<Record<string, FindingComment[]>>({})
  const [moduleSignoffs, setModuleSignoffs] = useState<ModuleSignoff[]>([])
  const [auditSignoff, setAuditSignoff] = useState<AuditSignoff | null>(null)
  const [members, setMembers] = useState<AuditMember[]>([])
  const [isOwner, setIsOwner] = useState(false)

  // Dashboard states for module overview
  const [moduleStats, setModuleStats] = useState<{
    totalFindings: number
    criticalFindings: number
    highFindings: number
    mediumFindings: number
    lowFindings: number
    analysesRun: number
    totalRows: number
    lastAnalysisDate: string | null
  } | null>(null)
  const [latestFindings, setLatestFindings] = useState<any[]>([])
  const [taxAccountMetrics, setTaxAccountMetrics] = useState<{
    negativeBalanceDays: number
    totalDeviationAmount: number
    latePayments: number
  } | null>(null)

  useEffect(() => {
    if (id) {
      fetchCaseData()
    }
  }, [id])

  // Function to recalculate module states when documents change
  const recalculateModuleStates = useCallback((docs: Document[], signoffs: ModuleSignoff[]) => {
    console.log('🔄 Recalculating module states with docs:', docs, 'signoffs:', signoffs);
    
    const moduleStatesTemp: Record<string, { status: keyof typeof moduleStatusLabels, documentsUploaded: number, documentsRequired: number }> = {}
    
    modules.forEach(module => {
      const moduleDocuments = docs?.filter(doc => doc.module_type === module.id) || []
      const uploaded = moduleDocuments.length
      const required = Math.max(1, uploaded) // At least show 1 as required if any uploaded
      
      // Check if module is signed off
      const isSignedOff = signoffs.some(signoff => signoff.module_id === module.id)
      
      console.log(`📊 Module ${module.id}: ${uploaded} documents, signed off: ${isSignedOff}`, moduleDocuments);
      
      let status: keyof typeof moduleStatusLabels = 'not_started'
      if (isSignedOff) {
        status = 'completed'
      } else if (uploaded > 0) {
        status = 'in_progress'
      }
      
      moduleStatesTemp[module.id] = {
        status,
        documentsUploaded: uploaded,
        documentsRequired: required
      }
    })
    
    console.log('📊 Final module states:', moduleStatesTemp);
    setModuleStates(moduleStatesTemp)
  }, [modules])

  // Fetch dashboard stats for module overview
  useEffect(() => {
    if (selectedModule && id) {
      fetchModuleStats()
      fetchLatestFindings()
      if (selectedModule === 'taxes_fees') {
        fetchTaxAccountMetrics()
      }
    }
  }, [selectedModule, id])

  const fetchModuleStats = async () => {
    if (!id || !selectedModule) return
    
    try {
      // Fetch only the LATEST revision for this module
      const { data: revisions } = await supabase
        .from('revisions')
        .select('id, created_at')
        .eq('audit_id', id)
        .eq('module_type', selectedModule)
        .order('created_at', { ascending: false })
        .limit(1)

      if (!revisions || revisions.length === 0) {
        setModuleStats(null)
        return
      }

      const latestRevision = revisions[0]

      // Fetch aggregate for ONLY this revision
      const { data: aggregate } = await supabase
        .from('aggregates')
        .select('summary_json')
        .eq('revision_id', latestRevision.id)
        .single()

      let criticalCount = 0
      let highCount = 0
      let mediumCount = 0
      let lowCount = 0
      let totalRows = 0

      if (aggregate?.summary_json) {
        const findings = (aggregate.summary_json as any)?.findings || []
        findings.forEach((f: any) => {
          if (f.severity === 'critical') criticalCount++
          else if (f.severity === 'high') highCount++
          else if (f.severity === 'medium') mediumCount++
          else if (f.severity === 'low') lowCount++
        })
        
        // Get total rows from aggregate summary
        totalRows = (aggregate.summary_json as any)?.totalRows || 0
      }

      // Count TOTAL number of revisions for "Analyses Run"
      const { count: totalRevisions } = await supabase
        .from('revisions')
        .select('id', { count: 'exact', head: true })
        .eq('audit_id', id)
        .eq('module_type', selectedModule)

      setModuleStats({
        totalFindings: criticalCount + highCount + mediumCount + lowCount,
        criticalFindings: criticalCount,
        highFindings: highCount,
        mediumFindings: mediumCount,
        lowFindings: lowCount,
        analysesRun: totalRevisions || 1,
        totalRows,
        lastAnalysisDate: latestRevision.created_at
      })
    } catch (err) {
      console.error('Error fetching module stats:', err)
    }
  }

  const fetchLatestFindings = async () => {
    if (!id || !selectedModule) return
    
    try {
      // Fetch only the latest revision
      const { data: latestRevision } = await supabase
        .from('revisions')
        .select('id, created_at')
        .eq('audit_id', id)
        .eq('module_type', selectedModule)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (!latestRevision) {
        setLatestFindings([])
        return
      }

      // Fetch aggregate for this revision
      const { data: aggregate } = await supabase
        .from('aggregates')
        .select('summary_json, created_at')
        .eq('revision_id', latestRevision.id)
        .single()

      if (!aggregate?.summary_json) {
        setLatestFindings([])
        return
      }

      const findings = (aggregate.summary_json as any)?.findings || []
      const criticalHighFindings = findings
        .filter((f: any) => f.severity === 'critical' || f.severity === 'high')
        .slice(0, 3)
        .map((f: any) => ({
          ...f,
          created_at: aggregate.created_at
        }))

      setLatestFindings(criticalHighFindings)
    } catch (err) {
      console.error('Error fetching latest findings:', err)
      setLatestFindings([])
    }
  }

  const fetchTaxAccountMetrics = async () => {
    if (!id || !selectedModule) return
    
    try {
      // Fetch only the latest revision
      const { data: latestRevision } = await supabase
        .from('revisions')
        .select('id')
        .eq('audit_id', id)
        .eq('module_type', 'taxes_fees')
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (!latestRevision) {
        setTaxAccountMetrics(null)
        return
      }

      const { data: aggregate } = await supabase
        .from('aggregates')
        .select('summary_json')
        .eq('revision_id', latestRevision.id)
        .single()

      if (!aggregate?.summary_json) {
        setTaxAccountMetrics(null)
        return
      }

      let negativeBalanceDays = 0
      let totalDeviationAmount = 0
      let latePayments = 0

      const findings = (aggregate.summary_json as any)?.findings || []
      findings.forEach((f: any) => {
        const message = (f.shortMessage || f.message || '').toLowerCase()
        const category = (f.category || '').toLowerCase()
        
        if (message.includes('negativt saldo') || category.includes('negativt saldo')) {
          negativeBalanceDays++
          if (f.amount) totalDeviationAmount += Math.abs(f.amount)
        }
        if (message.includes('sen betalning') || message.includes('försenad') || category.includes('sen')) {
          latePayments++
        }
      })

      setTaxAccountMetrics({
        negativeBalanceDays,
        totalDeviationAmount,
        latePayments
      })
    } catch (err) {
      console.error('Error fetching tax account metrics:', err)
      setTaxAccountMetrics(null)
    }
  }

  // Function to delete a document
  const deleteDocument = async (documentId: string) => {
    try {
      // Get document details first
      const docToDelete = documents.find(d => d.id === documentId)
      if (!docToDelete) return

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('audit-documents')
        .remove([docToDelete.storage_path])

      if (storageError) throw storageError

      // Delete from database
      const { error: dbError } = await supabase
        .from('audit_documents')
        .delete()
        .eq('id', documentId)

      if (dbError) throw dbError

      // Update local state
      const updatedDocuments = documents.filter(d => d.id !== documentId)
      setDocuments(updatedDocuments)
      recalculateModuleStates(updatedDocuments, moduleSignoffs)

      toast({
        title: 'Dokument raderat',
        description: `${docToDelete.original_filename} har raderats`,
      })
    } catch (error) {
      console.error('Delete error:', error)
      toast({
        title: 'Fel vid radering',
        description: error instanceof Error ? error.message : 'Ett fel uppstod',
        variant: 'destructive',
      })
    }
  }

  const fetchCaseData = async () => {
    try {
      // Fetch audit
      const { data: auditData, error: auditError } = await supabase
        .from('audits')
        .select('*')
        .eq('id', id)
        .single()

      if (auditError) throw auditError
      setAudit(auditData)
      setIsOwner(auditData.user_id === profile?.id || role === 'system_admin')

      // Fetch audit documents from the unified system
      const { data: documentsData, error: documentsError } = await supabase
        .from('audit_documents')
        .select('*')
        .eq('audit_id', id)

      // Fetch module signoffs
      const { data: moduleSignoffsData, error: moduleSignoffsError } = await supabase
        .from('module_signoffs')
        .select('*')
        .eq('audit_id', id)

      if (!moduleSignoffsError && moduleSignoffsData) {
        setModuleSignoffs(moduleSignoffsData)
      }

      if (!documentsError && documentsData) {
        console.log('📄 Documents fetched:', documentsData);
        console.log('📄 Documents by module:', documentsData.reduce((acc, doc) => {
          acc[doc.module_type] = (acc[doc.module_type] || 0) + 1;
          return acc;
        }, {} as Record<string, number>));
        
        setDocuments(documentsData)
        // Recalculate module states with the fresh document data
        recalculateModuleStates(documentsData, moduleSignoffsData || [])

        // Fetch analysis findings for this audit (used for KPI and list)
        const { data: findingsData, error: findingsError } = await supabase
          .from('audit_findings')
          .select('id, severity, finding_type, description, created_at, data_reference')
          .eq('audit_id', id)
          .order('created_at', { ascending: false })

        if (!findingsError && findingsData) {
          // Add module_id based on finding type for better categorization
          const findingsWithModule = findingsData.map((f: any) => ({
            ...f,
            module_id: f.finding_type.includes('late_payment') || 
                      f.finding_type.includes('tax') || 
                      f.finding_type.includes('interest') || 
                      f.finding_type.includes('amount_deviation') ? 'taxes_fees' : 'unknown'
          }))
          
          setAuditFindings(findingsWithModule)
          setAnalysisCount(findingsData.length)
          setAnalysisSeverity({
            high: findingsData.filter((f: any) => f.severity === 'high' || f.severity === 'critical').length,
            medium: findingsData.filter((f: any) => f.severity === 'medium').length,
            low: findingsData.filter((f: any) => f.severity === 'low').length,
          })
        }
      }

      // Fetch audit members
      const { data: membersData, error: membersError } = await supabase
        .from('audit_members')
        .select(`
          id,
          user_id,
          role,
          added_at,
          profiles:user_id (
            full_name,
            email,
            avatar_url
          )
        `)
        .eq('audit_id', id)

      if (!membersError && membersData) {
        const auditMembers: AuditMember[] = membersData.map(member => {
          const profileData = Array.isArray(member.profiles) ? member.profiles[0] : member.profiles
          return {
            id: member.id,
            user_id: member.user_id,
            role: member.role,
            added_at: member.added_at,
            full_name: profileData?.full_name,
            email: profileData?.email,
            avatar_url: profileData?.avatar_url
          }
        })

        // Add owner if not already in members
        if (!auditMembers.some(m => m.user_id === auditData.user_id)) {
          auditMembers.unshift({
            id: `owner_${auditData.user_id}`,
            user_id: auditData.user_id,
            role: 'owner',
            added_at: auditData.created_at,
            full_name: profile?.full_name
          })
        }

        setMembers(auditMembers)
      }

      // Initialize empty arrays for other data
      setAnalyses([])
      setComments([])
      setActionItems([])
      setFindings([])
      setFindingComments({})
      setFindingDecisions([])

    } catch (error) {
      console.error('Error fetching case data:', error)
      toast({
        title: "Fel",
        description: "Kunde inte hämta ärendedata",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const addComment = async () => {
    if (!newComment.trim()) return

    const comment: Comment = {
      id: `comment_${Date.now()}`,
      content: newComment,
      author: profile?.full_name || 'Okänd användare',
      created_at: new Date().toISOString()
    }

    setComments(prev => [...prev, comment])
    setNewComment('')
    
    toast({
      title: "Kommentar tillagd",
      description: "Din kommentar har sparats"
    })
  }

  // Function to sign off a module
  const signOffModule = async (moduleId: string, comment: string = '') => {
    try {
      const { data, error } = await supabase
        .from('module_signoffs')
        .insert({
          audit_id: id,
          module_id: moduleId,
          signed_off_by: profile?.id,
          comment: comment || null
        })
        .select()
        .single()

      if (error) throw error

      // Update local state
      const newSignoff: ModuleSignoff = {
        id: data.id,
        audit_id: id!,
        module_id: moduleId,
        signed_off_by: profile?.id!,
        signed_off_at: data.signed_off_at,
        comment: comment || undefined
      }

      const updatedSignoffs = [...moduleSignoffs, newSignoff]
      setModuleSignoffs(updatedSignoffs)
      
      // Recalculate module states with updated signoffs
      recalculateModuleStates(documents, updatedSignoffs)

      toast({
        title: "Modul avslutad",
        description: `${modules.find(m => m.id === moduleId) ? t(modules.find(m => m.id === moduleId)!.nameKey) : moduleId} har markerats som klar`,
      })
    } catch (error) {
      console.error('Signoff error:', error)
      toast({
        title: 'Fel vid avslut',
        description: error instanceof Error ? error.message : 'Ett fel uppstod',
        variant: 'destructive',
      })
    }
  }

  // Function to remove module signoff
  const removeModuleSignoff = async (moduleId: string) => {
    try {
      const signoffToRemove = moduleSignoffs.find(s => s.module_id === moduleId)
      if (!signoffToRemove) return

      const { error } = await supabase
        .from('module_signoffs')
        .delete()
        .eq('id', signoffToRemove.id)

      if (error) throw error

      // Update local state
      const updatedSignoffs = moduleSignoffs.filter(s => s.module_id !== moduleId)
      setModuleSignoffs(updatedSignoffs)
      
      // Recalculate module states with updated signoffs
      recalculateModuleStates(documents, updatedSignoffs)

      toast({
        title: "Modulavslut borttaget",
        description: `Avslut för ${modules.find(m => m.id === moduleId) ? t(modules.find(m => m.id === moduleId)!.nameKey) : moduleId} har tagits bort`,
      })
    } catch (error) {
      console.error('Remove signoff error:', error)
      toast({
        title: 'Fel vid borttagning',
        description: error instanceof Error ? error.message : 'Ett fel uppstod',
        variant: 'destructive',
      })
    }
  }

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 Byte'
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString())
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  // Removed - using formatWithUTC from @/lib/datetime

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!audit) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Ärende kunde inte hittas</p>
        <Button onClick={() => navigate('/cases')} className="mt-4">
          Tillbaka till ärenden
        </Button>
      </div>
    )
  }

  const renderModuleDetailView = () => {
    if (!selectedModule) return null
    
    const module = modules.find(m => m.id === selectedModule)
    const moduleState = moduleStates[selectedModule]
    const moduleDocuments = documents.filter(doc => doc.module_type === selectedModule)
    
    console.log('🎯 Module detail view:', {
      selectedModule,
      moduleState,
      totalDocuments: documents.length,
      moduleDocuments: moduleDocuments.length,
      allDocs: documents.map(d => ({ id: d.id, module_type: d.module_type, filename: d.original_filename }))
    });

    return (
      <div className="space-y-6">
        {/* Module Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedModule(null)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka till moduler
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold">{module ? t(module.nameKey) : ''}</h2>
              <Badge className={moduleStatusColors[moduleState.status]} variant="outline">
                {moduleStatusLabels[moduleState.status]}
              </Badge>
            </div>
          </div>
        </div>

        {/* Module Tabs */}
        <Tabs value={selectedModuleTab} onValueChange={setSelectedModuleTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Översikt</TabsTrigger>
            <TabsTrigger value="documents">Dokument</TabsTrigger>
            <TabsTrigger value="analyses">Analyser</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {/* KPI Cards */}
              {moduleStats && (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-blue-600 dark:text-blue-400">Totala fynd</p>
                          <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{moduleStats.totalFindings}</p>
                        </div>
                        <AlertCircle className="h-8 w-8 text-blue-600 dark:text-blue-400 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 border-red-200 dark:border-red-800">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-red-600 dark:text-red-400">Kritisk/Hög</p>
                          <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                            {moduleStats.criticalFindings + moduleStats.highFindings}
                          </p>
                        </div>
                        <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200 dark:border-green-800">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-green-600 dark:text-green-400">Analyser körda</p>
                          <p className="text-2xl font-bold text-green-900 dark:text-green-100">{moduleStats.analysesRun}</p>
                        </div>
                        <Brain className="h-8 w-8 text-green-600 dark:text-green-400 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200 dark:border-purple-800">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-purple-600 dark:text-purple-400">Rader analyserade</p>
                          <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                            {moduleStats.totalRows.toLocaleString()}
                          </p>
                        </div>
                        <BarChart3 className="h-8 w-8 text-purple-600 dark:text-purple-400 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Latest Findings */}
              {latestFindings.length > 0 && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-destructive" />
                        Senaste kritiska fynd
                      </CardTitle>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedModuleTab('analyses')}
                      >
                        Visa alla →
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {latestFindings.map((finding, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg bg-card">
                          <div className="mt-0.5">
                            {finding.severity === 'critical' ? (
                              <AlertCircle className="h-5 w-5 text-destructive" />
                            ) : (
                              <AlertTriangle className="h-5 w-5 text-warning" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={finding.severity === 'critical' ? 'destructive' : 'default'}>
                                {finding.severity === 'critical' ? 'Kritisk' : 'Hög'}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {formatWithUTC(finding.created_at)}
                              </span>
                            </div>
                            <p className="text-sm font-medium">{finding.shortMessage || finding.message}</p>
                            {finding.amount && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Belopp: {finding.amount.toLocaleString()} SEK
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Analysis Statistics */}
              {moduleStats && moduleStats.analysesRun > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-success" />
                      Analysstatistik
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Senaste analys:</span>
                          <span className="font-medium">
                            {moduleStats.lastAnalysisDate 
                              ? formatWithUTC(moduleStats.lastAnalysisDate)
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Totalt analyser:</span>
                          <span className="font-medium">{moduleStats.analysesRun}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Rader processade:</span>
                          <span className="font-medium">{moduleStats.totalRows.toLocaleString()}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-medium mb-2">Fördelning fynd:</div>
                        <div className="space-y-1">
                          {moduleStats.totalFindings > 0 && (
                            <>
                              <div className="flex items-center gap-2">
                                <div className="w-16 text-xs text-muted-foreground">Kritisk:</div>
                                <Progress 
                                  value={(moduleStats.criticalFindings / moduleStats.totalFindings) * 100} 
                                  className="h-2 flex-1"
                                />
                                <div className="w-8 text-xs font-medium text-right">
                                  {moduleStats.criticalFindings}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-16 text-xs text-muted-foreground">Hög:</div>
                                <Progress 
                                  value={(moduleStats.highFindings / moduleStats.totalFindings) * 100} 
                                  className="h-2 flex-1"
                                />
                                <div className="w-8 text-xs font-medium text-right">
                                  {moduleStats.highFindings}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-16 text-xs text-muted-foreground">Medel:</div>
                                <Progress 
                                  value={(moduleStats.mediumFindings / moduleStats.totalFindings) * 100} 
                                  className="h-2 flex-1"
                                />
                                <div className="w-8 text-xs font-medium text-right">
                                  {moduleStats.mediumFindings}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-16 text-xs text-muted-foreground">Låg:</div>
                                <Progress 
                                  value={(moduleStats.lowFindings / moduleStats.totalFindings) * 100} 
                                  className="h-2 flex-1"
                                />
                                <div className="w-8 text-xs font-medium text-right">
                                  {moduleStats.lowFindings}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Module-Specific Metrics (only for tax account) */}
              {selectedModule === 'taxes_fees' && taxAccountMetrics && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      Skattekontospecifika mått
                    </CardTitle>
                    <CardDescription>
                      Nyckeltal från skattekontoanalysen
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Dagar med negativt saldo</p>
                        <p className="text-2xl font-bold">{taxAccountMetrics.negativeBalanceDays}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Total avvikelse</p>
                        <p className="text-2xl font-bold">
                          {taxAccountMetrics.totalDeviationAmount.toLocaleString()} SEK
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Sena betalningar</p>
                        <p className="text-2xl font-bold">{taxAccountMetrics.latePayments}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick Actions */}
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="text-base">Snabbåtgärder</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start gap-2"
                      onClick={() => setSelectedModuleTab('documents')}
                    >
                      <Play className="h-4 w-4" />
                      Starta ny analys
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="w-full justify-start gap-2"
                      onClick={() => setSelectedModuleTab('analyses')}
                      disabled={!moduleStats || moduleStats.totalFindings === 0}
                    >
                      <AlertCircle className="h-4 w-4" />
                      Visa alla fynd ({moduleStats?.totalFindings || 0})
                    </Button>

                    <Button 
                      variant="outline" 
                      className="w-full justify-start gap-2"
                      onClick={() => setSelectedModuleTab('analyses')}
                      disabled={!moduleStats || moduleStats.totalFindings === 0}
                    >
                      <FileText className="h-4 w-4" />
                      Generera rapport
                    </Button>

                    <Button 
                      variant="outline" 
                      className="w-full justify-start gap-2"
                      onClick={() => setSelectedModuleTab('documents')}
                    >
                      <Upload className="h-4 w-4" />
                      Ladda upp data
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Original Overview Info (moved to bottom) */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <TrendingUp className="h-5 w-5" />
                    Om modulen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Denna modul granskar {module ? t(module.nameKey).toLowerCase() : ''} och relaterade processer.
                  </p>
                  <div className="grid gap-3">
                    <div className="flex justify-between">
                      <span className="text-sm">Status:</span>
                      <Badge className={moduleStatusColors[moduleState.status]} variant="outline">
                        {moduleStatusLabels[moduleState.status]}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Uppladdade dokument:</span>
                      <span className="text-sm font-medium">{moduleState.documentsUploaded}</span>
                    </div>
                    {moduleState.status === 'completed' && (() => {
                      const signoff = moduleSignoffs.find(s => s.module_id === selectedModule)
                      return signoff ? (
                        <div className="space-y-2 pt-2 border-t">
                          <div className="flex justify-between">
                            <span className="text-sm">Avslutad av:</span>
                            <span className="text-sm font-medium">{profile?.full_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Datum:</span>
                            <span className="text-sm font-medium">
                              {formatWithUTC(signoff.signed_off_at)}
                            </span>
                          </div>
                          {signoff.comment && (
                            <div className="space-y-1">
                              <span className="text-sm">Kommentar:</span>
                              <p className="text-sm text-muted-foreground">{signoff.comment}</p>
                            </div>
                          )}
                        </div>
                      ) : null
                    })()}
                  </div>
                  {moduleState.status !== 'completed' && moduleState.documentsUploaded > 0 && (
                    <div className="pt-4 border-t">
                      <Button 
                        onClick={() => signOffModule(selectedModule || '')}
                        className="w-full gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Markera som klar
                      </Button>
                    </div>
                  )}
                  {moduleState.status === 'completed' && (
                    <div className="pt-4 border-t">
                      <Button 
                        onClick={() => removeModuleSignoff(selectedModule || '')}
                        variant="outline"
                        className="w-full gap-2"
                      >
                        <X className="h-4 w-4" />
                        Ångra avslut
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Analyses Tab */}
          <TabsContent value="analyses">
            <ModuleAnalysesTab 
              auditId={id || ''}
              moduleId={selectedModule || ''}
              onStartAnalysis={() => setSelectedModuleTab('documents')}
            />
          </TabsContent>

          {/* Documents Tab - Upload & Analysis */}
          {selectedModule === 'taxes_fees' && (
            <TabsContent value="documents">
                  <GenericBatchAnalysisModule 
                    auditId={id || ''} 
                    moduleType="taxes_fees"
                    documents={documents}
                    onDocumentDeleted={fetchCaseData}
                    onAnalysisStarted={(revisionId) => {
                      console.log('[CaseDetailPage] Analysis started, navigating to analyses tab:', revisionId);
                      setSelectedModuleTab('analyses');
                    }}
                  />
            </TabsContent>
          )}
          
          {selectedModule === 'revenue' && (
            <TabsContent value="documents">
                  <GenericBatchAnalysisModule 
                    auditId={id || ''} 
                    moduleType="revenue"
                    documents={documents}
                    onDocumentDeleted={fetchCaseData}
                    onAnalysisStarted={(revisionId) => {
                      console.log('[CaseDetailPage] Revenue analysis started, navigating to analyses tab:', revisionId);
                      setSelectedModuleTab('analyses');
                    }}
                  />
            </TabsContent>
          )}
        </Tabs>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Show Module Detail View or Main Case View */}
      {selectedModule ? renderModuleDetailView() : (
        <>
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/cases')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Tillbaka
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold tracking-tight">{audit.title}</h1>
                <Badge className={statusColors[audit.status]}>
                  {audit.status}
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1">{audit.description}</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => navigate(`/final-report/${id}`)}
                className="gap-2"
              >
                <FileText className="h-4 w-4" />
                Slutrapport
              </Button>
              <Button className="gap-2">
                <Download className="h-4 w-4" />
                Exportera data
              </Button>
            </div>
          </div>

          {/* Module Navigation */}
          <div className="border rounded-lg p-4 bg-card">
            <h2 className="text-lg font-semibold mb-4">Moduler</h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {modules.map((module) => {
                const state = moduleStates[module.id]
                const signoff = moduleSignoffs.find(s => s.module_id === module.id)
                return (
                  <Card 
                    key={module.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${selectedModule === module.id ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setSelectedModule(selectedModule === module.id ? null : module.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-sm">{t(module.nameKey)}</h3>
                        <Badge className={moduleStatusColors[state.status]} variant="outline">
                          {moduleStatusLabels[state.status]}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mb-2">
                        {state.documentsUploaded} dokument uppladdade
                      </div>
                      {state.documentsUploaded > 0 && (
                        <Progress 
                          value={state.status === 'completed' ? 100 : 75} 
                          className="h-1 mb-2" 
                        />
                      )}
                      {state.status === 'completed' && signoff && (
                        <div className="text-xs text-muted-foreground">
                          Avslutad {formatWithUTC(signoff.signed_off_at)}
                        </div>
                      )}
                      {state.status === 'in_progress' && (
                        <div className="flex gap-1 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-xs"
                            onClick={(e) => {
                              e.stopPropagation()
                              signOffModule(module.id)
                            }}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Avsluta
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>

          {/* Overview Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Dokument</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{documents.length}</div>
                <p className="text-xs text-muted-foreground">
                  totalt uppladdade
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Analyser</CardTitle>
                <Brain className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analysisCount}</div>
                <div className="flex gap-3 mt-1 text-xs">
                  <span className="text-red-600">Hög: {analysisSeverity.high}</span>
                  <span className="text-yellow-600">Medel: {analysisSeverity.medium}</span>
                  <span className="text-green-600">Låg: {analysisSeverity.low}</span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Action Items</CardTitle>
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{actionItems.length}</div>
                <p className="text-xs text-muted-foreground">
                  {actionItems.filter(a => a.status === 'open').length} öppna
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Kommentarer</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{comments.length}</div>
                <p className="text-xs text-muted-foreground">
                  totalt
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Main Tabs */}
          <Tabs defaultValue="documents" className="space-y-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="documents">Dokument</TabsTrigger>
              <TabsTrigger value="analyses">Analyser</TabsTrigger>
              <TabsTrigger value="actions">Action Items</TabsTrigger>
              <TabsTrigger value="comments">Kommentarer</TabsTrigger>
              <TabsTrigger value="members">Medlemmar</TabsTrigger>
            </TabsList>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <Card>
                <CardHeader>
                  <CardTitle>Alla dokument</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {documents.length > 0 ? (
                      documents.map((doc) => (
                        <div key={doc.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <FileText className="h-4 w-4" />
                                <h4 className="font-medium">{doc.original_filename}</h4>
                                {doc.module_type && (
                                  <Badge variant="outline" className="text-xs">
                                    {(() => {
                                      const moduleObj = modules.find(m => m.id === doc.module_type)
                                      return moduleObj ? t(moduleObj.nameKey) : doc.module_type
                                    })()}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatFileSize(doc.size_bytes)} • {formatWithUTC(doc.created_at)}
                              </div>
                            </div>
                             <div className="flex gap-2">
                               <Button size="sm" variant="outline" className="gap-2">
                                 <Eye className="h-4 w-4" />
                                 Visa
                               </Button>
                               <Button size="sm" variant="outline" className="gap-2">
                                 <Download className="h-4 w-4" />
                                 Ladda ner
                               </Button>
                               <AlertDialog>
                                 <AlertDialogTrigger asChild>
                                   <Button 
                                     size="sm" 
                                     variant="outline" 
                                     className="gap-2 text-destructive hover:text-destructive-foreground hover:bg-destructive"
                                   >
                                     <Trash2 className="h-4 w-4" />
                                     Radera
                                   </Button>
                                 </AlertDialogTrigger>
                                 <AlertDialogContent>
                                   <AlertDialogHeader>
                                     <AlertDialogTitle>Radera dokument</AlertDialogTitle>
                                     <AlertDialogDescription>
                                       Är du säker på att du vill radera "{doc.original_filename}"? 
                                       Denna åtgärd kan inte ångras.
                                     </AlertDialogDescription>
                                   </AlertDialogHeader>
                                   <AlertDialogFooter>
                                     <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                     <AlertDialogAction 
                                       onClick={() => deleteDocument(doc.id)}
                                       className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                     >
                                       Radera
                                     </AlertDialogAction>
                                   </AlertDialogFooter>
                                 </AlertDialogContent>
                               </AlertDialog>
                             </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        Inga dokument uppladdade
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Analyses Tab */}
            <TabsContent value="analyses">
              <Card>
                <CardHeader>
                  <CardTitle>Analyser</CardTitle>
                </CardHeader>
                <CardContent>
                  {auditFindings.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Inga analyser har körts än. Välj en modul för att starta analyser.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {auditFindings.map((f) => (
                        <div key={f.id} className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline">
                              {f.severity === 'critical' ? 'Kritisk' : f.severity === 'high' ? 'Hög' : f.severity === 'medium' ? 'Medel' : 'Låg'}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">{f.finding_type}</Badge>
                            {f.data_reference?.source_file && (
                              <Badge variant="outline" className="text-xs">📄 {f.data_reference.source_file}</Badge>
                            )}
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                              {(() => {
                                const moduleObj = modules.find(m => m.id === f.module_id)
                                return moduleObj ? t(moduleObj.nameKey) : 'Okänd modul'
                              })()}
                            </Badge>
                          </div>
                          <div className="text-sm font-medium">{f.description}</div>
                          <div className="text-xs text-muted-foreground">{formatWithUTC(f.created_at)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Action Items Tab */}
            <TabsContent value="actions">
              <Card>
                <CardHeader>
                  <CardTitle>Action Items</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-muted-foreground">
                    Inga action items har skapats än
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Comments Tab */}
            <TabsContent value="comments">
              <Card>
                <CardHeader>
                  <CardTitle>Kommentarer</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add Comment */}
                  <div className="space-y-2">
                    <Label htmlFor="comment">Lägg till kommentar</Label>
                    <Textarea
                      id="comment"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Skriv en kommentar..."
                      className="min-h-[80px]"
                    />
                    <div className="flex justify-end">
                      <Button onClick={addComment} disabled={!newComment.trim()}>
                        Lägg till kommentar
                      </Button>
                    </div>
                  </div>

                  {/* Comments List */}
                  <div className="space-y-3">
                    {comments.map((comment) => (
                      <div key={comment.id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-sm">{comment.author}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatWithUTC(comment.created_at)}
                          </div>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    ))}
                    {comments.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        Inga kommentarer än
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members">
              <MembersManager
                auditId={id}
                members={members}
                onMembersChange={setMembers}
                currentUserId={profile?.id || ''}
                isOwner={isOwner}
                organizationId={organization?.id}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  )
}